import { Box } from '@mui/material';
import { AxisBottom, AxisRight } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { LinearGradient } from '@visx/gradient';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleBand, scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, Bar, Line, LinePath } from '@visx/shape';
import { useTooltip } from '@visx/tooltip';
import { bisector, extent, max, min } from 'd3-array';
import React, { useCallback, useEffect, useId, useMemo, useRef } from 'react';

const parseDate = (s) => {
  if (s instanceof Date) return s;
  if (typeof s !== 'string') return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getX = (d) => d._date;
const getY = (d) => d._value;
const bisectDate = bisector((d) => d._date).left;

const TEXT_COLOR = '#6b7280';
const GRID_COLOR = '#eef0f3';
const FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const compactFmt = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const pctFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const fullFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

const formatAxisDate = (d) =>
  d ? d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '';
const formatFullDate = (d) =>
  d
    ? d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

const VisxChartInner = ({
  width,
  height,
  chart,
  chartMargin,
  showLegend,
  showCrosshair,
  isPercent,
  onRendered,
}) => {
  const gradientId = useId();
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip();

  const seriesDef = chart?.series?.[0] || {};
  const seriesType = seriesDef.type || 'line';
  const setting = seriesDef.setting || {};
  const legendLabel = seriesDef.legend?.label || '';
  const accent =
    setting.line?.stroke || setting.rect?.fill || setting.area?.fill || '#0A66C2';

  const points = useMemo(() => {
    const raw = Array.isArray(chart?.series_data) ? chart.series_data : [];
    return raw
      .map((d) => ({ ...d, _date: parseDate(d.recordDate), _value: Number(d.value) }))
      .filter((d) => d._date && Number.isFinite(d._value))
      .sort((a, b) => a._date - b._date);
  }, [chart?.series_data]);

  const margin = useMemo(
    () => ({ top: 18, right: 56, bottom: 34, left: 8, ...(chartMargin || {}) }),
    [chartMargin]
  );

  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const formatValue = useCallback(
    (v) => (isPercent ? `${pctFmt.format(v)}%` : fullFmt.format(v)),
    [isPercent]
  );
  const formatTick = useCallback(
    (v) => (isPercent ? `${pctFmt.format(v)}%` : compactFmt.format(v)),
    [isPercent]
  );

  const xScale = useMemo(() => {
    const domain = points.length ? extent(points, getX) : [new Date(), new Date()];
    return scaleTime({ domain, range: [0, innerWidth], nice: false });
  }, [points, innerWidth]);

  const xBand = useMemo(
    () =>
      scaleBand({
        domain: points.map((d) => d.recordDate),
        range: [0, innerWidth],
        padding: 0.45,
      }),
    [points, innerWidth]
  );

  const yScale = useMemo(() => {
    const minV = points.length ? Math.min(0, min(points, getY)) : 0;
    const maxV = points.length ? Math.max(0, max(points, getY)) : 1;
    const span = maxV - minV || 1;
    const pad = span * 0.12;
    return scaleLinear({
      domain: [minV - (minV < 0 ? pad : 0), maxV + pad],
      range: [innerHeight, 0],
      nice: true,
    });
  }, [points, innerHeight]);

  const renderedRef = useRef(false);
  useEffect(() => {
    if (!renderedRef.current && points.length && innerWidth > 0) {
      renderedRef.current = true;
      if (typeof onRendered === 'function') onRendered();
    }
  }, [points.length, innerWidth, onRendered]);

  const handleTooltip = useCallback(
    (event) => {
      if (!points.length) return;
      const coords = localPoint(event);
      if (!coords) return;
      const x = coords.x - margin.left;

      let d;
      if (seriesType === 'bar') {
        const step = xBand.step();
        let nearest = points[0];
        let nearestDist = Infinity;
        for (let i = 0; i < points.length; i += 1) {
          const center = (xBand(points[i].recordDate) ?? 0) + xBand.bandwidth() / 2;
          const dist = Math.abs(center - x);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = points[i];
          }
        }
        if (nearestDist > step) {
          hideTooltip();
          return;
        }
        d = nearest;
      } else {
        const x0 = xScale.invert(x);
        const index = bisectDate(points, x0, 1);
        const d0 = points[index - 1];
        const d1 = points[index];
        d = d0;
        if (d0 && d1) d = x0 - getX(d0) > getX(d1) - x0 ? d1 : d0;
      }

      if (!d) return;

      const cx =
        seriesType === 'bar'
          ? (xBand(d.recordDate) ?? 0) + xBand.bandwidth() / 2
          : xScale(getX(d));

      showTooltip({
        tooltipData: d,
        tooltipLeft: cx + margin.left,
        tooltipTop: yScale(getY(d)) + margin.top,
      });
    },
    [points, seriesType, xScale, xBand, yScale, margin.left, margin.top, showTooltip, hideTooltip]
  );

  const zeroY = yScale(0);
  const barWidth = xBand.bandwidth ? xBand.bandwidth() : 0;

  if (width < 10 || height < 10) return null;

  const isEmpty = points.length === 0;

  return (
    <div style={{ position: 'relative', fontFamily: FONT }}>
      <svg width={width} height={height} role="img" aria-label={legendLabel || 'chart'}>
        <LinearGradient
          id={gradientId}
          from={setting.area?.fill || accent}
          to={setting.area?.fill || accent}
          fromOpacity={0.28}
          toOpacity={0.02}
        />

        <Group left={margin.left} top={margin.top}>
          <GridRows
            scale={yScale}
            width={innerWidth}
            numTicks={4}
            stroke={GRID_COLOR}
            strokeWidth={1}
            pointerEvents="none"
          />

          {isEmpty && (
            <text x={innerWidth / 2} y={innerHeight / 2} textAnchor="middle" fontSize={12} fill="#9ca3af">
              No data available
            </text>
          )}

          {!isEmpty && seriesType === 'area' && (
            <>
              <AreaClosed
                data={points}
                x={(d) => xScale(getX(d))}
                y={(d) => yScale(getY(d))}
                yScale={yScale}
                curve={curveMonotoneX}
                fill={`url(#${gradientId})`}
                stroke="transparent"
              />
              <LinePath
                data={points}
                x={(d) => xScale(getX(d))}
                y={(d) => yScale(getY(d))}
                curve={curveMonotoneX}
                stroke={accent}
                strokeWidth={2}
                strokeLinecap="round"
              />
            </>
          )}

          {!isEmpty && seriesType === 'line' && (
            <LinePath
              data={points}
              x={(d) => xScale(getX(d))}
              y={(d) => yScale(getY(d))}
              curve={curveMonotoneX}
              stroke={accent}
              strokeWidth={2}
              strokeLinecap="round"
            />
          )}

          {!isEmpty &&
            seriesType === 'bar' &&
            points.map((d) => {
              const cx = (xBand(d.recordDate) ?? 0) + barWidth / 2;
              const valY = yScale(getY(d));
              const barY = getY(d) >= 0 ? valY : zeroY;
              const barH = Math.max(1, Math.abs(valY - zeroY));
              const active = tooltipData && tooltipData.recordDate === d.recordDate;
              return (
                <Bar
                  key={`bar-${d.recordDate}`}
                  x={cx - barWidth / 2}
                  y={barY}
                  width={barWidth}
                  height={barH}
                  rx={Math.min(3, barWidth / 2)}
                  fill={accent}
                  fillOpacity={active ? 1 : 0.85}
                />
              );
            })}

          {showCrosshair && !isEmpty && tooltipData && (
            <g pointerEvents="none">
              <Line
                from={{ x: tooltipLeft - margin.left, y: 0 }}
                to={{ x: tooltipLeft - margin.left, y: innerHeight }}
                stroke={accent}
                strokeWidth={1}
                strokeOpacity={0.35}
              />
              {seriesType !== 'bar' && (
                <circle
                  cx={tooltipLeft - margin.left}
                  cy={tooltipTop - margin.top}
                  r={4.5}
                  fill="#fff"
                  stroke={accent}
                  strokeWidth={2}
                />
              )}
            </g>
          )}

          <AxisRight
            left={innerWidth}
            scale={yScale}
            numTicks={4}
            hideAxisLine
            hideTicks
            tickFormat={(v) => formatTick(v)}
            tickLabelProps={() => ({
              fill: TEXT_COLOR,
              fontSize: 11,
              fontFamily: FONT,
              textAnchor: 'start',
              dx: '0.5em',
              dy: '0.32em',
            })}
          />

          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={Math.min(6, points.length || 1)}
            hideTicks
            hideAxisLine
            stroke="transparent"
            tickFormat={(d) => formatAxisDate(d)}
            tickLabelProps={() => ({
              fill: TEXT_COLOR,
              fontSize: 11,
              fontFamily: FONT,
              textAnchor: 'middle',
            })}
          />
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onMouseMove={handleTooltip}
            onTouchMove={handleTooltip}
            onTouchStart={handleTooltip}
            onMouseLeave={hideTooltip}
          />
        </Group>
      </svg>

      {showLegend && legendLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: margin.left,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '.72rem',
            color: TEXT_COLOR,
          }}
        >
          <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: accent }} />
          {legendLabel}
        </Box>
      )}

      {!isEmpty && tooltipData && (
        <div
          style={{
            position: 'absolute',
            top: tooltipTop,
            left: tooltipLeft,
            transform: 'translate(-50%, calc(-100% - 12px))',
            background: 'rgba(20, 22, 34, 0.92)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            color: '#fff',
            padding: '7px 10px',
            borderRadius: 8,
            fontSize: 11.5,
            fontFamily: FONT,
            lineHeight: 1.45,
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13 }}>{formatValue(getY(tooltipData))}</div>
          <div style={{ opacity: 0.75 }}>{formatFullDate(getX(tooltipData))}</div>
        </div>
      )}
    </div>
  );
};

const StockHoldingsChart = ({
  chart,
  showChart = true,
  showLegend = false,
  showCrosshair = true,
  chartMargin,
  onRendered,
  redrawOnClick, noHeader, scale, themes, type, axisTextMargin,
}) => {
  if (!showChart || !chart) return null;

  const ySeries = chart?.ySeries || chart?.code || '';
  const isPercent = /PORTPCT/i.test(ySeries);
  const aspRatio = chart?.chartSetting?.aspRatio || 2.1;

  return (
    <ParentSize>
      {({ width }) => {
        if (!width) return null;
        const height = Math.max(190, Math.round(width / aspRatio));
        return (
          <VisxChartInner
            width={width}
            height={height}
            chart={chart}
            chartMargin={chartMargin}
            showLegend={showLegend}
            showCrosshair={showCrosshair}
            isPercent={isPercent}
            onRendered={onRendered}
          />
        );
      }}
    </ParentSize>
  );
};

export default React.memo(StockHoldingsChart);